/**
 * Ability input buffer (story `q-combat-game-feel`; docs/design/combat.md §Control model,
 * "Input buffer"). A press made a moment before the ability is ready is HELD and sent at ready,
 * instead of reaching the server early, being refused `onCooldown`, and being lost.
 *
 * The rule:
 * - The client's cooldown view (`AbilityCooldowns`, armed from the server's own `readyInMs`) says
 *   the ability is ready, or is further than {@link ABILITY_BUFFER_WINDOW_MS} from ready ⇒ the
 *   press is sent AT ONCE, exactly as before (the server still judges it).
 * - Within the window ⇒ the press is held and sent {@link ABILITY_BUFFER_MARGIN_MS} after ready.
 * - One press is held at a time: the newest press replaces a held one (the player changed their
 *   mind); a press sent at once drops a held one too.
 * - A sent press the server refuses `onCooldown` with `readyInMs` inside the window (the cooldown
 *   armed in the round trip after a cast, before the client heard of it) is held ONCE more. A
 *   held press that is refused again is dropped, so a refusal can never loop.
 *
 * Never sent twice: a press is either sent now or held, never both; firing a held press clears
 * it first; the refusal path only re-holds a press the server did NOT cast.
 *
 * Why a margin, and why late is safe: `readyAt` is the time the client HEARD `readyInMs` plus
 * `readyInMs`, so it already trails the server's own ready instant by the one-way latency, and the
 * press then travels another one-way trip. The margin covers the server's tick quantisation.
 *
 * Presentation only: the press is the same `abilityIntent` the HUD always sent; the soft target is
 * re-picked by the caller's `send` at fire time, not at press time. Pure apart from the injected
 * ports, so it runs under `node --test`.
 */

import type { AbilityCooldowns } from './abilitySlotState.js';

/** A press this close to ready (ms) is held rather than sent (cheatsheet range 100–150 ms). */
export const ABILITY_BUFFER_WINDOW_MS = 150;

/** A held press fires this long after the client's `readyAt` (server tick quantisation). */
export const ABILITY_BUFFER_MARGIN_MS = 30;

export interface AbilityBufferPorts {
  /** Send the press now (the caller names the soft target picked at THIS moment). */
  send(abilityId: string): void;
  /** Run `fn` after `ms`; returns a cancel. */
  schedule(fn: () => void, ms: number): () => void;
  /** Wall clock in ms, the same basis as `AbilityCooldown.readyAt` (`Date.now()`). */
  now(): number;
}

/** What a press did. */
export type AbilityPressOutcome = 'sent' | 'held';

interface Held {
  abilityId: string;
  /** True when this hold is already the one retry after a refusal. */
  retry: boolean;
  cancel: () => void;
}

/** ms until `abilityId` is ready by the client's view; 0 when ready or unknown. */
export function msUntilReady(cooldowns: AbilityCooldowns, abilityId: string, now: number): number {
  const cd = cooldowns[abilityId];
  return cd === undefined ? 0 : Math.max(0, cd.readyAt - now);
}

/** Whether a press `leftMs` from ready is held (inside the window) rather than sent now. */
export function shouldHold(leftMs: number): boolean {
  return leftMs > 0 && leftMs <= ABILITY_BUFFER_WINDOW_MS;
}

export class AbilityPressBuffer {
  private held: Held | null = null;
  /** The last press that went to the server, and whether it was already a retry. */
  private lastSent: { abilityId: string; retry: boolean } | null = null;

  constructor(private readonly ports: AbilityBufferPorts) {}

  /** A press of `abilityId` (keys, bar, touch): sent now, or held until ready. */
  press(abilityId: string, cooldowns: AbilityCooldowns): AbilityPressOutcome {
    const left = msUntilReady(cooldowns, abilityId, this.ports.now());
    this.cancel();
    if (shouldHold(left)) {
      this.hold(abilityId, left + ABILITY_BUFFER_MARGIN_MS, false);
      return 'held';
    }
    this.fire(abilityId, false);
    return 'sent';
  }

  /**
   * The server answered a press of `abilityId`. An `onCooldown` refusal within the window re-holds
   * the press once (unless a newer press is already held); anything else is left alone.
   */
  answered(abilityId: string, ok: boolean, reason: string | undefined, readyInMs?: number): void {
    const last = this.lastSent;
    if (last === null || last.abilityId !== abilityId) return;
    this.lastSent = null;
    if (ok || reason !== 'onCooldown' || last.retry || this.held !== null) return;
    if (readyInMs === undefined || !shouldHold(readyInMs)) return;
    this.hold(abilityId, readyInMs + ABILITY_BUFFER_MARGIN_MS, true);
  }

  /** The ability held right now, if any (diagnostics / tests). */
  heldAbility(): string | null {
    return this.held?.abilityId ?? null;
  }

  /** Drop a held press (a loadout swap, death, leaving the world). */
  cancel(): void {
    if (this.held === null) return;
    this.held.cancel();
    this.held = null;
  }

  private hold(abilityId: string, delayMs: number, retry: boolean): void {
    const held: Held = {
      abilityId,
      retry,
      cancel: () => undefined,
    };
    held.cancel = this.ports.schedule(() => {
      if (this.held !== held) return; // replaced or cancelled
      this.held = null;
      this.fire(abilityId, retry);
    }, delayMs);
    this.held = held;
  }

  private fire(abilityId: string, retry: boolean): void {
    this.lastSent = { abilityId, retry };
    this.ports.send(abilityId);
  }
}
